import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaFilter, FaTrashAlt } from "react-icons/fa";
import { FiChevronLeft, FiChevronRight, FiEye, FiX } from "react-icons/fi";
import Select from "react-select";

import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { useGetAllCustomersQuery } from "../toolkitServices/customerApi";
import { useGetDeletedInvoicesQuery } from "../toolkitServices/invoiceApi";

const fmt2 = (n) => Number(n || 0).toFixed(2);
const money = (n) => `₹ ${fmt2(n)}`;
const dIN = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-IN");
};
const dtIN = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "-"
    : date.toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      });
};

const THEME = {
  primary: "#6b59c6",
  ring: "#7c3aed",
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 12,
    borderColor: state.isFocused ? THEME.ring : "#e5e7eb",
    boxShadow: state.isFocused ? `${THEME.ring}22 0 0 0 3px` : "none",
    "&:hover": { borderColor: THEME.ring },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 70,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

const paginationSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    height: 32,
    borderRadius: 8,
    borderColor: state.isFocused ? "#9481ce" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 1px rgba(148,129,206,.25)" : "none",
    fontSize: 10,
  }),
  valueContainer: (base) => ({ ...base, height: 32, padding: "0 5px" }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (base) => ({ ...base, zIndex: 70, fontSize: 10 }),
  menuList: (base) => ({ ...base, maxHeight: 160, padding: 2 }),
  option: (base, state) => ({
    ...base,
    padding: "6px 8px",
    backgroundColor: state.isSelected
      ? "#9481ce"
      : state.isFocused
        ? "#ede9fe"
        : "white",
    color: state.isSelected ? "white" : "#334155",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

function Badge({ children, tone = "gray" }) {
  const tones = {
    gray: "bg-slate-100 text-slate-700 ring-slate-200",
    red: "bg-red-50 text-red-700 ring-red-200",
    purple: "bg-[#efeafd] text-[#4b3d91] ring-[#dcd3fb]",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full ring-1 ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

function MiniStat({ label, value, tone = "slate" }) {
  const tones = {
    slate: "bg-white border-slate-200 text-slate-900",
    purple: "bg-[#f7f4ff] border-[#e6defd] text-[#4b3d91]",
    red: "bg-red-50 border-red-200 text-red-700",
  };
  return (
    <div className={`rounded-2xl border px-3 py-2 ${tones[tone]}`}>
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="text-sm font-extrabold leading-5">{value}</div>
    </div>
  );
}

function DetailRow({ label, value, tone = "slate" }) {
  const toneCls = {
    slate: "text-slate-800",
    red: "text-red-700",
    purple: "text-[#4b3d91]",
  };
  return (
    <div className="flex items-center justify-between gap-4 py-2 border-b last:border-b-0">
      <div className="text-sm text-slate-600">{label}</div>
      <div className={`text-sm font-bold text-right ${toneCls[tone] || toneCls.slate}`}>
        {value}
      </div>
    </div>
  );
}

function SummaryStrip({ totalDeleted, pageTotal }) {
  return (
    <div className="shrink-0 border-b bg-gradient-to-r from-slate-50 via-violet-50/60 to-slate-50 px-2.5 py-2 sm:px-4 sm:py-2.5">
      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        <SummaryCard label="Deleted Invoices" value={totalDeleted} />
        <SummaryCard label="Current Page Total" value={money(pageTotal)} deleted />
      </div>
    </div>
  );
}

function TableSkeletonRows() {
  return Array.from({ length: 8 }).map((_, i) => (
    <tr key={`sk-${i}`} className="border-t">
      <td className="px-3 py-3">
        <div className="h-4 w-24 bg-slate-200 rounded animate-pulse" />
      </td>
      <td className="px-3 py-3">
        <div className="h-4 w-20 bg-slate-200 rounded animate-pulse" />
      </td>
      <td className="hidden sm:table-cell px-3 py-3">
        <div className="h-4 w-20 bg-slate-200 rounded animate-pulse" />
      </td>
      <td className="hidden md:table-cell px-3 py-3">
        <div className="h-4 w-28 bg-slate-200 rounded animate-pulse" />
      </td>
      <td className="hidden lg:table-cell px-3 py-3">
        <div className="h-4 w-24 bg-slate-200 rounded animate-pulse" />
      </td>
      <td className="px-3 py-3 text-right">
        <div className="h-4 w-24 bg-slate-200 rounded animate-pulse ml-auto" />
      </td>
      <td className="hidden xl:table-cell px-3 py-3">
        <div className="h-4 w-24 bg-slate-200 rounded animate-pulse" />
      </td>
      <td className="hidden xl:table-cell px-3 py-3">
        <div className="h-4 w-28 bg-slate-200 rounded animate-pulse" />
      </td>
      <td className="px-3 py-3 text-center">
        <div className="h-8 w-16 bg-slate-200 rounded-full animate-pulse mx-auto" />
      </td>
    </tr>
  ));
}

/* ------------------- Details Modal ------------------- */
function InvoiceDetailsModal({ open, onClose, invoice }) {
  const items = invoice?.stockEntries || [];
  const totals = invoice?.totals || {};
  const deletedByName =
    invoice?.deletedBy?.userName || invoice?.deletedByName || "-";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/50" />

          <motion.div
            className="relative w-full max-w-[780px] 2xl:max-w-[860px] bg-white rounded-3xl shadow-2xl overflow-hidden"
            initial={{ scale: 0.96, y: 10, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.96, y: 10, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(90deg, rgba(148,129,206,1) 0%, rgba(107,89,198,1) 100%)",
                }}
              />
              <div className="relative px-5 py-4 text-white">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base md:text-lg font-extrabold truncate">
                        {invoice?.invCode || "-"}
                      </h3>
                      <Badge tone="red">DELETED</Badge>
                      <Badge tone="purple">
                        {invoice?.branchId?.branchName || "-"}
                      </Badge>
                    </div>

                    <div className="mt-1 text-xs text-white/85">
                      Invoice: <span className="font-semibold">{dIN(invoice?.invDate)}</span> •
                      Deleted:{" "}
                      <span className="font-semibold">
                        {dtIN(invoice?.deletedAt || invoice?.createdAt)}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={onClose}
                    className="shrink-0 p-2 rounded-full bg-white/15 hover:bg-white/25 transition"
                    aria-label="Close"
                    title="Close"
                  >
                    <FiX />
                  </button>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2">
                  <MiniStat
                    label="Grand Total"
                    value={money(totals?.grandTotal)}
                    tone="purple"
                  />
                  <MiniStat
                    label="Outstanding"
                    value={money(totals?.outstanding)}
                    tone="red"
                  />
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-5 max-h-[62vh] sm:max-h-[72vh] overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="rounded-2xl border p-4 bg-[#f7f4ff]">
                  <div className="text-sm font-extrabold text-[#4b3d91]">
                    Deletion Info
                  </div>
                  <div className="mt-3 space-y-1">
                    <DetailRow label="Deleted By" value={deletedByName} />
                    <DetailRow
                      label="Reason"
                      value={invoice?.deleteReason || "-"}
                      tone="red"
                    />
                    <DetailRow
                      label="Deleted At"
                      value={dtIN(invoice?.deletedAt || invoice?.createdAt)}
                    />
                  </div>
                </div>

                <div className="rounded-2xl border p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-extrabold text-slate-900">
                      Totals
                    </div>
                    <Badge tone="purple">Summary</Badge>
                  </div>
                  <div className="mt-3">
                    <DetailRow label="Items" value={`${totals?.items || 0}`} />
                    <DetailRow label="Qty" value={`${totals?.qty || 0}`} />
                    <DetailRow label="Net Wt" value={`${fmt2(totals?.netWt)} g`} />
                    <DetailRow
                      label="Sub Total"
                      value={money(totals?.subTotal)}
                      tone="purple"
                    />
                    <DetailRow
                      label="GST Total"
                      value={money(totals?.gstTotal)}
                      tone="purple"
                    />
                    <DetailRow
                      label="Discount"
                      value={money(totals?.discount)}
                    />
                    <DetailRow
                      label="Grand Total"
                      value={money(totals?.grandTotal)}
                      tone="purple"
                    />
                    <DetailRow label="Paid" value={money(totals?.paid)} />
                    <DetailRow
                      label="Outstanding"
                      value={money(totals?.outstanding)}
                      tone="red"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-2xl border overflow-hidden">
                <div className="bg-[#efeafd] px-4 py-3 flex items-center justify-between">
                  <div className="text-sm font-extrabold text-[#4b3d91]">Items</div>
                  <Badge tone="purple">{items.length} item(s)</Badge>
                </div>

                <div className="divide-y">
                  {items.length === 0 && (
                    <div className="p-5 text-sm text-slate-500 text-center">
                      No item details
                    </div>
                  )}

                  {items.map((it, idx) => (
                    <div key={`${it?.labelledItemId || idx}`} className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-extrabold text-slate-900">
                            {it?.itemCode || "-"}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {it?.categoryName || "-"} • {it?.productName || "-"} •{" "}
                            {it?.designName || "-"} • {it?.purityName || "-"}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-extrabold text-slate-900">
                            {money(it?.totalItemAmount)}
                          </div>
                          <div className="text-xs text-slate-500">
                            Net {fmt2(it?.netWt)} g
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 grid grid-cols-3 gap-2">
                        <MiniStat
                          label="Rate/g"
                          value={money(it?.ratePerGram)}
                          tone="purple"
                        />
                        <MiniStat
                          label="Item Amt"
                          value={money(
                            it?.itemAmountAfterDiscount ?? it?.itemAmount
                          )}
                          tone="purple"
                        />
                        <MiniStat
                          label="GST"
                          value={money(it?.itemGstAmount)}
                          tone="purple"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-5 flex justify-end gap-2">
                <button
                  onClick={onClose}
                  className="px-5 py-2 rounded-full text-sm font-extrabold border hover:bg-slate-50 transition"
                >
                  Close
                </button>
                <button
                  onClick={onClose}
                  className="px-5 py-2 rounded-full text-sm font-extrabold text-white"
                  style={{ backgroundColor: THEME.primary }}
                >
                  Done
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ------------------- Main Screen ------------------- */
export default function DeletedInvoiceReport() {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [branchId, setBranchId] = useState("");
  const [customerId, setCustomerId] = useState("");

  const [draftBranchId, setDraftBranchId] = useState("");
  const [draftCustomerId, setDraftCustomerId] = useState("");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const { data, isFetching, isError, error } = useGetDeletedInvoicesQuery({
    branchId,
    customerId,
    page,
    limit,
  });

  const { data: branches } = useGetAllBranchesQuery();
  const { data: customers } = useGetAllCustomersQuery();

  const branchOptions = useMemo(
    () =>
      branches?.data?.map((b) => ({
        label: b.branchName,
        value: b._id,
      })) || [],
    [branches]
  );

  const customerOptions = useMemo(
    () =>
      customers?.data?.map((c) => ({
        label: c.customerName || c.customerId || "Customer",
        value: c._id,
      })) || [],
    [customers]
  );

  const rows = useMemo(() => data?.rows || [], [data]);
  const totalDeleted = data?.total || 0;
  const currentPage = Number(data?.page || page || 1);
  const totalPages = Math.max(1, Math.ceil(totalDeleted / limit));
  const hasPrevPage = currentPage > 1;
  const hasNextPage = currentPage < totalPages;
  const showingFrom =
    totalDeleted === 0 ? 0 : (currentPage - 1) * limit + 1;
  const showingTo = Math.min(currentPage * limit, totalDeleted);
  const pageTotal = rows.reduce(
    (a, r) => a + Number(r?.totals?.grandTotal || 0),
    0
  );

  const openDetails = (inv) => {
    setSelectedInvoice(inv);
    setDetailOpen(true);
  };

  const openFilterModal = () => {
    setDraftBranchId(branchId);
    setDraftCustomerId(customerId);
    setIsFilterOpen(true);
  };

  const clearAppliedFilters = () => {
    setBranchId("");
    setCustomerId("");
    setPage(1);
  };

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsFilterOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, []);

  return (
    <div className="flex h-[calc(100vh-140px)] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg md:h-[calc(100vh-70px)]">
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between gap-3 bg-gradient-to-r from-[#6b59c6] via-[#806dcc] to-[#9481ce] px-3 py-2.5 text-white shadow-sm sm:px-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20">
            <FaTrashAlt className="text-sm" />
          </span>
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold tracking-tight sm:text-lg">
              Deleted Invoice Report
            </h2>
            <p className="truncate text-[9px] text-violet-100 sm:text-[11px]">
              Deleted invoice audit history
            </p>
          </div>
          {isFetching && (
            <span className="hidden items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] sm:inline-flex">
              <span className="h-2.5 w-2.5 animate-spin rounded-full border border-white border-t-transparent" />
              refreshing…
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <span className="hidden rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-medium ring-1 ring-white/10 sm:inline-flex">
            {totalDeleted} deleted
          </span>
          <button
            type="button"
            onClick={openFilterModal}
            className="inline-flex h-9 w-9 items-center justify-center gap-1.5 rounded-xl bg-white/15 text-white ring-1 ring-white/20 transition hover:bg-white/25 sm:w-auto sm:rounded-full sm:px-3"
            title="Filters"
            aria-label="Open filters"
          >
            <FaFilter className="text-xs" />
            <span className="hidden text-[11px] font-semibold sm:inline">
              Filters
            </span>
          </button>
        </div>
      </div>

      {/* Active filters */}
      {(branchId || customerId) && (
        <div className="flex shrink-0 items-center gap-2 border-b bg-slate-50 px-3 py-1.5 sm:px-4">
          <div className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {branchId && (
              <FilterChip
                label={`Branch: ${
                  branchOptions.find((branch) => branch.value === branchId)
                    ?.label || "Selected"
                }`}
              />
            )}
            {customerId && (
              <FilterChip
                label={`Customer: ${
                  customerOptions.find(
                    (customer) => customer.value === customerId,
                  )?.label || "Selected"
                }`}
              />
            )}
          </div>
          <button
            type="button"
            onClick={clearAppliedFilters}
            className="shrink-0 text-[10px] font-semibold text-[#6b59c6] hover:underline"
          >
            Clear all
          </button>
        </div>
      )}

      {/* SUMMARY */}
      {!isFetching && rows.length > 0 && (
        <div className="shrink-0">
          <SummaryStrip totalDeleted={totalDeleted} pageTotal={pageTotal} />
        </div>
      )}

      {/* Deleted invoices */}
      <div className="min-h-0 flex-1 overflow-auto bg-slate-50/70 p-2.5 sm:p-4">
        <div className="h-full overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full min-w-[720px] text-xs sm:min-w-full sm:text-sm">
            <thead className="sticky top-0 z-10 bg-[#efeafd] text-[10px] uppercase tracking-wide text-[#4b3d91] sm:text-[11px]">
              <tr>
                <th className="px-3 py-2.5 text-left">Invoice</th>
                <th className="px-3 py-2.5 text-left">Invoice Date</th>
                <th className="hidden border-l-2 border-l-rose-100 bg-rose-50/50 px-3 py-2.5 text-left text-rose-700 sm:table-cell">
                  Deleted On
                </th>
                <th className="hidden px-3 py-2.5 text-left md:table-cell">
                  Customer
                </th>
                <th className="hidden px-3 py-2.5 text-left lg:table-cell">
                  Branch
                </th>
                <th className="border-l-2 border-l-violet-200 bg-violet-50/60 px-3 py-2.5 text-right">
                  Grand Total
                </th>
                <th className="hidden bg-rose-50/40 px-3 py-2.5 text-left text-rose-700 xl:table-cell">
                  Deleted By
                </th>
                <th className="hidden bg-rose-50/40 px-3 py-2.5 text-left text-rose-700 xl:table-cell">
                  Reason
                </th>
                <th className="px-3 py-2.5 text-center">View</th>
              </tr>
            </thead>

            <tbody className="bg-white text-slate-700">
              {isFetching && <TableSkeletonRows />}

              {!isFetching && isError && (
                <tr>
                  <td colSpan={9} className="py-10 text-center text-rose-600">
                    {error?.data?.message ||
                      "Unable to load deleted invoices"}
                  </td>
                </tr>
              )}

              {!isFetching && !isError && rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-10 text-center text-slate-500">
                    No deleted invoices found
                  </td>
                </tr>
              )}

              {!isFetching && !isError &&
                rows.map((inv) => (
                  <tr
                    key={inv._id}
                    className="border-t border-slate-100 transition hover:bg-rose-50/30"
                  >
                    <td className="px-3 py-3">
                      <div className="font-semibold text-slate-900 truncate max-w-[120px] sm:max-w-none">
                        {inv.invCode}
                      </div>
                      <div className="sm:hidden text-[10px] text-slate-500 mt-0.5">
                        {inv.branchId?.branchName || "-"}
                      </div>
                    </td>

                    <td className="px-3 py-3 whitespace-nowrap">
                      <div>{dIN(inv.invDate)}</div>
                      <div className="sm:hidden text-[10px] text-red-500 mt-0.5">
                        Deleted: {dIN(inv.deletedAt || inv.createdAt)}
                      </div>
                    </td>

                    <td className="hidden whitespace-nowrap border-l-2 border-l-rose-100 bg-rose-50/20 px-3 py-3 font-medium text-rose-700 sm:table-cell">
                      {dIN(inv.deletedAt || inv.createdAt)}
                    </td>

                    <td className="hidden md:table-cell px-3 py-3">
                      <div className="max-w-[220px] truncate">
                        {inv.customerId?.customerId} - {inv.customerId?.customerName || "-"}
                      </div>
                    </td>

                    <td className="hidden lg:table-cell px-3 py-3">
                      {inv.branchId?.branchName || "-"}
                    </td>

                    <td className="whitespace-nowrap border-l-2 border-l-violet-100 bg-violet-50/30 px-3 py-3 text-right font-bold tabular-nums text-[#4b3d91]">
                      {money(inv.totals?.grandTotal)}
                    </td>

                    <td className="hidden bg-rose-50/20 px-3 py-3 xl:table-cell">
                      {inv?.deletedBy?.userName || inv?.deletedByName || "-"}
                    </td>

                    <td className="hidden max-w-[260px] bg-rose-50/20 px-3 py-3 text-rose-700 xl:table-cell">
                      <span className="line-clamp-1">{inv.deleteReason || "-"}</span>
                      <div className="text-[11px] text-slate-400">full reason in view</div>
                    </td>

                    <td className="px-3 py-3 text-center">
                      <button
                        onClick={() => openDetails(inv)}
                        className="inline-flex items-center justify-center gap-1 rounded-full bg-[#6b59c6] px-2.5 py-1.5 text-[11px] font-semibold text-white transition hover:bg-[#5948ae] sm:px-3 sm:text-xs"
                        title="View details"
                      >
                        <FiEye />
                        <span className="hidden sm:inline">View</span>
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer pagination */}
      {!isFetching && !isError && rows.length > 0 && (
        <div className="flex shrink-0 items-center justify-between gap-2 border-t bg-white px-2 py-1.5 text-[10px] sm:gap-3 sm:px-4 sm:py-2 sm:text-[11px]">
          <div className="hidden text-slate-600 sm:block">
            Showing <span className="font-semibold">{showingFrom}</span>–
            <span className="font-semibold">{showingTo}</span> of{" "}
            <span className="font-semibold">{totalDeleted}</span>
          </div>

          <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start sm:gap-3">
            <div className="flex shrink-0 items-center gap-1">
              <span className="hidden text-slate-500 sm:inline">Rows:</span>
              <Select
                value={{ value: limit, label: String(limit) }}
                options={[10, 20, 50, 100].map((number) => ({
                  value: number,
                  label: String(number),
                }))}
                onChange={(option) => {
                  setLimit(Number(option?.value) || 20);
                  setPage(1);
                }}
                isSearchable={false}
                menuPlacement="top"
                menuPosition="fixed"
                menuPortalTarget={document.body}
                menuShouldScrollIntoView={false}
                styles={paginationSelectStyles}
                className="w-[54px]"
                aria-label="Rows per page"
              />
              <span className="text-[9px] text-slate-400 sm:hidden">
                / page
              </span>
            </div>

            <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:flex-none">
              <button
                type="button"
                onClick={() => setPage((current) => Math.max(current - 1, 1))}
                disabled={!hasPrevPage}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-base disabled:opacity-40 sm:h-auto sm:w-auto sm:gap-1 sm:rounded-md sm:px-2 sm:py-1 sm:text-[11px]"
                aria-label="Previous page"
              >
                <FiChevronLeft />
                <span className="hidden sm:inline">Prev</span>
              </button>

              <span className="text-slate-600">
                <span className="flex items-center gap-1 sm:hidden">
                  <Select
                    value={{ value: currentPage, label: String(currentPage) }}
                    options={Array.from(
                      { length: totalPages },
                      (_, index) => ({
                        value: index + 1,
                        label: String(index + 1),
                      }),
                    )}
                    onChange={(option) => setPage(Number(option?.value) || 1)}
                    isSearchable={false}
                    menuPlacement="top"
                    menuPosition="fixed"
                    menuPortalTarget={document.body}
                    menuShouldScrollIntoView={false}
                    styles={paginationSelectStyles}
                    className="w-[52px]"
                    aria-label="Select page number"
                  />
                  <span>/ {totalPages}</span>
                </span>
                <span className="hidden sm:inline">
                  Page <span className="font-semibold">{currentPage}</span> of{" "}
                  <span className="font-semibold">{totalPages}</span>
                </span>
              </span>

              <button
                type="button"
                onClick={() =>
                  setPage((current) => Math.min(current + 1, totalPages))
                }
                disabled={!hasNextPage}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-base disabled:opacity-40 sm:h-auto sm:w-auto sm:gap-1 sm:rounded-md sm:px-2 sm:py-1 sm:text-[11px]"
                aria-label="Next page"
              >
                <span className="hidden sm:inline">Next</span>
                <FiChevronRight />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter panel */}
      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            className="fixed inset-0 z-[9998] flex items-end justify-center bg-slate-950/40 p-3 backdrop-blur-[1px] sm:items-center sm:p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsFilterOpen(false)}
          >
            <motion.div
              className="w-full max-w-[520px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
              initial={{ y: 30, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 30, opacity: 0, scale: 0.98 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between bg-gradient-to-r from-[#6b59c6] to-[#9481ce] px-4 py-3 text-white">
                <div>
                  <h3 className="text-sm font-semibold">
                    Deleted Invoice Filters
                  </h3>
                  <p className="mt-0.5 text-[10px] text-violet-100">
                    Filter by branch and customer
                  </p>
                </div>
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/15 transition hover:bg-white/25"
                  onClick={() => setIsFilterOpen(false)}
                  aria-label="Close filters"
                >
                  <FiX />
                </button>
              </div>

              <div className="space-y-3 p-4">
                <div>
                  <label className="mb-1 block text-[10px] font-medium text-slate-600">
                    Branch
                  </label>
                  <Select
                    styles={selectStyles}
                    options={branchOptions}
                    menuPortalTarget={document.body}
                    value={
                      draftBranchId
                        ? branchOptions.find((x) => x.value === draftBranchId) || null
                        : null
                    }
                    onChange={(o) => setDraftBranchId(o?.value || "")}
                    placeholder="Select branch"
                    isClearable
                  />
                </div>

                <div>
                  <label className="mb-1 block text-[10px] font-medium text-slate-600">
                    Customer
                  </label>
                  <Select
                    styles={selectStyles}
                    options={customerOptions}
                    menuPortalTarget={document.body}
                    value={
                      draftCustomerId
                        ? customerOptions.find((x) => x.value === draftCustomerId) || null
                        : null
                    }
                    onChange={(o) => setDraftCustomerId(o?.value || "")}
                    placeholder="Select customer"
                    isClearable
                  />
                </div>
              </div>

              <div className="flex items-center justify-between gap-2 border-t bg-slate-50 px-4 py-3">
                <button
                  type="button"
                  onClick={() => {
                    setDraftBranchId("");
                    setDraftCustomerId("");
                  }}
                  className="text-[11px] font-semibold text-slate-500 hover:text-slate-700"
                >
                  Reset
                </button>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsFilterOpen(false)}
                    className="rounded-full border border-slate-200 bg-white px-4 py-2 text-[11px] font-semibold text-slate-600"
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setBranchId(draftBranchId);
                      setCustomerId(draftCustomerId);
                      setPage(1);
                      setIsFilterOpen(false);
                    }}
                    className="rounded-full bg-[#6b59c6] px-5 py-2 text-[11px] font-semibold text-white shadow-sm transition hover:bg-[#5948ae]"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* DETAILS MODAL */}
      <InvoiceDetailsModal
        open={detailOpen}
        invoice={selectedInvoice}
        onClose={() => {
          setDetailOpen(false);
          setSelectedInvoice(null);
        }}
      />
    </div>
  );
}

function SummaryCard({ label, value, deleted = false }) {
  return (
    <div
      className={`min-w-0 rounded-xl border px-3 py-2 text-center shadow-sm ${
        deleted
          ? "border-rose-100 bg-rose-50 text-rose-700"
          : "border-violet-100 bg-white text-[#4b3d91]"
      }`}
    >
      <p className="truncate text-[9px] font-semibold uppercase tracking-wide opacity-80 sm:text-[10px]">
        {label}
      </p>
      <p className="mt-0.5 truncate text-[11px] font-bold tabular-nums sm:text-sm">
        {value}
      </p>
    </div>
  );
}

function FilterChip({ label }) {
  return (
    <span className="shrink-0 rounded-full border border-violet-100 bg-white px-2.5 py-1 text-[10px] font-medium text-slate-600">
      {label}
    </span>
  );
}
